// PortRequestDetail.jsx
//
// Phase E: the post-submit timeline view. Once a port_request has been
// submitted to Twilio (status >= submitted), customers land here when they
// click their row in the Port In tab instead of being routed back into the
// wizard. Owns:
//
//   1. Hero timeline with five nodes (Submitted, Documents Verified,
//      Carrier Review, Scheduled, Completed) and plain-English captions
//      per state. Renders a separate canceled / exception variant when
//      the port hit a terminal failure.
//   2. Per-number status table (Twilio reports per-number outcomes on
//      batches with mixed results; we surface them so the customer can
//      see exactly which lines moved and which stuck).
//   3. Exception banner with "Edit and resubmit" CTA when Twilio rejected
//      the port. Resubmit re-opens the wizard at Step 5 so the customer
//      can fix the failing field and re-fire submit(). Twilio handles
//      its own LOA email flow on the other side of submit, so there is
//      nothing for SurFox to re-issue on resubmit.
//   4. Cancel button when status allows (submitted / in_carrier_review /
//      scheduled / exception, and current date < requested_foc_date).
//
// State ownership: this component fetches its own port_request + events
// via `get` so the page survives a page refresh without depending on
// PortInTab's list cache. It also polls `get` every 30s while the port is
// in a non-terminal state so the timeline reflects Twilio webhooks landing
// without needing a hard refresh.
//
// All copy: no em dashes (per project convention).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
  Clock,
  Calendar,
  Phone,
  Mail,
  FileSignature,
  Edit3,
  ArrowLeftRight,
  RefreshCw,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getPortRequest, cancelPortRequest } from '../../../../../lib/portInClient';
import PortInWizard from './PortInWizard';

// ============================================================================
// Status -> timeline mapping
// ============================================================================
//
// Five timeline nodes. Each port_request status maps to exactly one "active"
// node (the one we render as in-progress) and an implicit set of "complete"
// nodes (everything before it).
//
// Terminal states (completed / canceled / exception) render dedicated states
// rather than slotting into the linear timeline.

const TIMELINE_NODES = [
  {
    key: 'submitted',
    label: 'Submitted',
    description: 'Your port request has been sent to Twilio.',
  },
  {
    key: 'documents_verified',
    label: 'Documents verified',
    description: 'Twilio confirmed the LOA and carrier bill look good.',
  },
  {
    key: 'carrier_review',
    label: 'Carrier review',
    description: 'Your losing carrier is reviewing the request. They typically respond in 5 to 7 business days.',
  },
  {
    key: 'scheduled',
    label: 'Scheduled',
    description: 'The losing carrier locked in a port date. Your numbers stay live until then.',
  },
  {
    key: 'completed',
    label: 'Completed',
    description: 'Your numbers are live on SurFox. Inbound calls and SMS now flow through your account.',
  },
];

// Map our port_requests.status enum to a timeline-node index (0..4). Pre-submit
// states (draft / awaiting_*) never land here because PortInTab routes them
// back to the wizard.
function statusToNodeIndex(status) {
  switch (status) {
    case 'submitted':
    case 'submitting':
      return 0;
    case 'in_carrier_review':
      return 2;
    case 'scheduled':
      return 3;
    case 'completed':
      return 4;
    case 'exception':
    case 'canceled':
      // Terminal-failure variants render their own state. Return the last
      // confirmed-progress node from the events so the timeline still tells
      // the story of how far the port got.
      return -1;
    default:
      // Anything else (a stale row hand-inserted with synthetic statuses)
      // still shows the timeline at node 0 so the operator can see the
      // status badge + status_message rather than a blank screen.
      return 0;
  }
}

// Map status to a hero badge style for the page header.
const HERO_STATUS = {
  submitted: { label: 'Submitted', tone: 'blue' },
  submitting: { label: 'Submitting', tone: 'blue' },
  in_carrier_review: { label: 'Carrier review', tone: 'indigo' },
  scheduled: { label: 'Scheduled', tone: 'emerald' },
  completed: { label: 'Completed', tone: 'emerald' },
  exception: { label: 'Needs attention', tone: 'red' },
  canceled: { label: 'Canceled', tone: 'slate' },
};

const TONE_CLASSES = {
  blue: 'bg-blue-100 text-blue-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  emerald: 'bg-emerald-100 text-emerald-800',
  red: 'bg-red-100 text-red-800',
  slate: 'bg-slate-100 text-slate-600',
};

// ============================================================================

export default function PortRequestDetail({ portRequestId, onClose, onChange }) {
  const [portRequest, setPortRequest] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [showResubmit, setShowResubmit] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    const { data, error: err } = await getPortRequest(portRequestId);
    if (!silent) setRefreshing(false);
    if (err) {
      setError(err.message || 'Failed to load port request.');
      return;
    }
    const pr = data?.portRequest;
    if (pr) {
      setPortRequest(pr);
      setEvents(Array.isArray(pr.events) ? pr.events : []);
      setError(null);
    }
  }, [portRequestId]);

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Poll while the port is mid-flight. Skip when the status is terminal
  // (completed / canceled / exception) since those won't change without
  // user action. 30s is the sweet spot: webhook lag rarely exceeds it,
  // and the cost is one cheap edge function read per minute per open tab.
  useEffect(() => {
    if (!portRequest) return;
    const TERMINAL = new Set(['completed', 'canceled', 'exception']);
    if (TERMINAL.has(portRequest.status)) return;
    const id = setInterval(() => refresh(true), 30_000);
    return () => clearInterval(id);
  }, [portRequest, refresh]);

  const handleCancelSubmit = async () => {
    if (!portRequest) return;
    setCanceling(true);
    setCancelError(null);
    const { data, error: err } = await cancelPortRequest(
      portRequest.id,
      cancelReason.trim() || 'Customer requested cancellation.',
    );
    setCanceling(false);
    if (err) {
      // supabase-js wraps non-2xx in error.context — try to surface body.error
      const ctx = err.context;
      let body = null;
      try {
        if (ctx?.body && typeof ctx.body === 'string') body = JSON.parse(ctx.body);
        else if (ctx?.body) body = ctx.body;
      } catch (_e) { /* ignore */ }
      setCancelError(body?.error || err.message || 'Could not cancel the port.');
      return;
    }
    if (data?.success || data?.alreadyCanceled) {
      setShowCancelConfirm(false);
      setCancelReason('');
      onChange?.();
      refresh();
    } else {
      setCancelError('Cancel request returned an unexpected response.');
    }
  };

  // ------------- Loading / error / empty -------------

  if (loading) {
    return (
      <DetailShell onClose={onClose}>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      </DetailShell>
    );
  }

  if (error || !portRequest) {
    return (
      <DetailShell onClose={onClose}>
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error || 'Port request not found.'}</span>
        </div>
      </DetailShell>
    );
  }

  return (
    <>
      <DetailShell onClose={onClose} onRefresh={() => refresh()} refreshing={refreshing}>
        <Header portRequest={portRequest} />

        {portRequest.status === 'exception' && (
          <ExceptionBanner
            portRequest={portRequest}
            onResubmit={() => setShowResubmit(true)}
          />
        )}

        {portRequest.status === 'canceled' && <CanceledBanner portRequest={portRequest} />}

        {portRequest.status === 'completed' && <CompletedBanner portRequest={portRequest} />}

        {/* Hero timeline — hide when the port hit a terminal failure since
            those have their own banner that tells a clearer story. */}
        {!['exception', 'canceled'].includes(portRequest.status) && (
          <Timeline portRequest={portRequest} events={events} />
        )}

        <ScheduleSummary portRequest={portRequest} />

        <NumberStatusTable portRequest={portRequest} events={events} />

        <NotificationEmails portRequest={portRequest} />

        <EventTimeline events={events} />

        {/* Cancel button visible on cancelable, pre-FOC-date statuses */}
        {isCancelable(portRequest) && (
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setShowCancelConfirm(true)}
              className="text-xs text-red-600 hover:text-red-700 underline-offset-2 hover:underline"
            >
              Cancel this port request
            </button>
          </div>
        )}
      </DetailShell>

      {showResubmit && (
        <PortInWizard
          portRequestId={portRequest.id}
          onClose={() => {
            setShowResubmit(false);
            refresh();
          }}
          onCompleted={() => {
            setShowResubmit(false);
            refresh();
            onChange?.();
          }}
        />
      )}

      {showCancelConfirm && (
        <CancelConfirmModal
          portRequest={portRequest}
          reason={cancelReason}
          onReasonChange={setCancelReason}
          onConfirm={handleCancelSubmit}
          onClose={() => {
            if (canceling) return;
            setShowCancelConfirm(false);
            setCancelError(null);
            setCancelReason('');
          }}
          canceling={canceling}
          error={cancelError}
        />
      )}
    </>
  );
}

// ============================================================================
// Modal shell
// ============================================================================

function DetailShell({ children, onClose, onRefresh, refreshing }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <AnimatePresence>
        <motion.div
          key="port-request-detail"
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.18 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col"
        >
          <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                type="button"
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                aria-label="Back"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <h2 className="text-sm font-semibold text-slate-900">Port request</h2>
            </div>
            <div className="flex items-center gap-1">
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  type="button"
                  disabled={refreshing}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                  aria-label="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              )}
              <button
                onClick={onClose}
                type="button"
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">{children}</div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// Hero header
// ============================================================================

function Header({ portRequest }) {
  const meta = HERO_STATUS[portRequest.status] || { label: portRequest.status, tone: 'slate' };
  const numbers = Array.isArray(portRequest.numbers) ? portRequest.numbers : [];
  const firstNumber = numbers[0]?.phone_number;
  const numberCount = numbers.length;

  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <ArrowLeftRight className="w-5 h-5 text-orange-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${TONE_CLASSES[meta.tone]}`}>
            {meta.label}
          </span>
          {portRequest.twilio_port_in_sid && (
            <span className="text-[11px] text-slate-400 font-mono">
              {portRequest.twilio_port_in_sid}
            </span>
          )}
        </div>
        <h3 className="text-base font-semibold text-slate-900 truncate">
          {firstNumber
            ? `${formatForDisplay(firstNumber)}${numberCount > 1 ? ` and ${numberCount - 1} more` : ''}`
            : 'Port request'}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Started {formatDateTime(portRequest.created_at)}
          {portRequest.submitted_at && (
            <span>{` · Submitted ${formatDateTime(portRequest.submitted_at)}`}</span>
          )}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Timeline (linear, 5 nodes)
// ============================================================================

function Timeline({ portRequest }) {
  const activeIdx = statusToNodeIndex(portRequest.status);

  // Documents-verified node is "implied complete" the moment we land on
  // submitted because the bill SID was a precondition for that status.
  // Phase F: pre-rip-out this also required the LOA SID, but Twilio now
  // auto-generates the LOA on their side so the bill is the only document
  // SurFox uploads. If we ever surface a Twilio-side bill-rejection state,
  // we can refine this with an event probe.
  const docsVerifiedComplete = !!portRequest.bill_twilio_document_sid;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2">
        {TIMELINE_NODES.map((node, i) => {
          let state = 'pending';
          if (i < activeIdx) state = 'done';
          else if (i === activeIdx) state = 'active';
          if (i === 1 && docsVerifiedComplete && activeIdx >= 0) state = 'done';
          return (
            <React.Fragment key={node.key}>
              <Node state={state} label={node.label} />
              {i < TIMELINE_NODES.length - 1 && (
                <div
                  className={`flex-1 h-0.5 ${
                    i < activeIdx ? 'bg-emerald-300' : 'bg-slate-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Plain-English caption for the active node */}
      {activeIdx >= 0 && (
        <p className="text-xs text-slate-600 mt-4 leading-relaxed">
          {TIMELINE_NODES[activeIdx]?.description}
        </p>
      )}

      {portRequest.status_message && portRequest.status !== 'exception' && portRequest.status !== 'canceled' && (
        <p className="text-[11px] text-slate-400 mt-1">{portRequest.status_message}</p>
      )}
    </div>
  );
}

function Node({ state, label }) {
  if (state === 'done') {
    return (
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white">
          <CheckCircle2 className="w-4 h-4" />
        </div>
        <span className="text-[10px] text-emerald-700 font-medium text-center max-w-[70px] leading-tight">
          {label}
        </span>
      </div>
    );
  }
  if (state === 'active') {
    return (
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white animate-pulse">
          <Clock className="w-3.5 h-3.5" />
        </div>
        <span className="text-[10px] text-orange-700 font-semibold text-center max-w-[70px] leading-tight">
          {label}
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1 flex-shrink-0">
      <div className="w-7 h-7 rounded-full border-2 border-slate-300 bg-white" />
      <span className="text-[10px] text-slate-400 text-center max-w-[70px] leading-tight">{label}</span>
    </div>
  );
}

// ============================================================================
// Exception state with "Edit and resubmit"
// ============================================================================

function ExceptionBanner({ portRequest, onResubmit }) {
  const rawMessage = portRequest.status_message || portRequest.rejection_reason;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 space-y-3">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-900">This port needs your attention</p>
          <p className="text-xs text-red-700 mt-1 break-words">
            {rawMessage || 'Twilio or the losing carrier rejected this port. Review the details below and resubmit.'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onResubmit}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg"
        >
          <Edit3 className="w-3.5 h-3.5" />
          Edit and resubmit
        </button>
        <span className="text-[11px] text-red-700/80">
          Your LOA stays valid. We will just re-fire the submit after you fix the issue.
        </span>
      </div>
    </div>
  );
}

function CanceledBanner({ portRequest }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 flex items-start gap-2">
      <XCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700">This port was canceled</p>
        <p className="text-xs text-slate-500 mt-1">
          {portRequest.status_message || 'The request was canceled.'}
          {portRequest.canceled_at && (
            <span>{` Canceled ${formatDateTime(portRequest.canceled_at)}.`}</span>
          )}
        </p>
      </div>
    </div>
  );
}

function CompletedBanner({ portRequest }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 flex items-start gap-2">
      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-emerald-900">Your numbers are live on SurFox</p>
        <p className="text-xs text-emerald-700 mt-1">
          Inbound calls and SMS now flow through your SurFox account. The numbers are in the Active Numbers tab and attached to your messaging service.
          {portRequest.completed_at && (
            <span>{` Completed ${formatDateTime(portRequest.completed_at)}.`}</span>
          )}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Schedule summary card
// ============================================================================

function ScheduleSummary({ portRequest }) {
  const requested = portRequest.requested_foc_date;
  const actual = portRequest.actual_foc_date;
  if (!requested && !actual) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-2 text-xs font-medium text-slate-700">
        <Calendar className="w-3.5 h-3.5 text-slate-400" />
        Port schedule
      </div>
      <div className="space-y-1 text-xs text-slate-700">
        {requested && (
          <p>
            <span className="text-slate-500">Requested date: </span>
            <span className="font-medium">{formatDateOnly(requested)}</span>
          </p>
        )}
        {actual && (
          <p>
            <span className="text-slate-500">Carrier-confirmed date: </span>
            <span className="font-medium">{formatDateOnly(actual)}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Per-number status table
// ============================================================================
//
// Twilio reports per-number outcomes on batches with mixed results (one
// number ports, another doesn't because PIN was wrong on that line). We
// surface them by reading individual_status off each number entry. When
// Twilio hasn't reported per-number outcomes yet, every row inherits the
// batch-level status.

function NumberStatusTable({ portRequest, events }) {
  const numbers = Array.isArray(portRequest.numbers) ? portRequest.numbers : [];

  // Probe events for per-number wire-up successes so the rows that are
  // already live read as "Live" even when the parent row hasn't been
  // re-fetched yet. Must run before any conditional return per
  // react-hooks/rules-of-hooks.
  const wiredUpPhones = useMemo(() => {
    const set = new Set();
    for (const e of events ?? []) {
      if (e.event_type === 'completed_number_wired' && e.payload?.phoneNumber) {
        set.add(e.payload.phoneNumber);
      }
    }
    return set;
  }, [events]);

  if (numbers.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-3 text-xs font-medium text-slate-700">
        <Phone className="w-3.5 h-3.5 text-slate-400" />
        Numbers
      </div>
      <ul className="divide-y divide-slate-100">
        {numbers.map((n) => {
          const phone = n.phone_number;
          const live = wiredUpPhones.has(phone) || portRequest.status === 'completed';
          const perNumberStatus = n.individual_status || (live ? 'completed' : portRequest.status);
          return (
            <li
              key={phone}
              className="flex items-center justify-between py-2 first:pt-0 last:pb-0 text-xs"
            >
              <div className="flex flex-col">
                <span className="font-mono text-slate-900">{formatForDisplay(phone)}</span>
                <span className="text-slate-400 text-[11px]">
                  {n.losing_carrier || 'Unknown carrier'}
                </span>
              </div>
              <NumberStatusBadge status={perNumberStatus} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NumberStatusBadge({ status }) {
  // Reuse the HERO_STATUS palette and fall through to a "pending" look.
  const meta = HERO_STATUS[status];
  if (meta) {
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${TONE_CLASSES[meta.tone]}`}>
        {meta.label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600">
      {labelize(status)}
    </span>
  );
}

// ============================================================================
// Misc detail cards
// ============================================================================

function NotificationEmails({ portRequest }) {
  const emails = Array.isArray(portRequest.notification_emails) ? portRequest.notification_emails : [];
  if (emails.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-2 text-xs font-medium text-slate-700">
        <Mail className="w-3.5 h-3.5 text-slate-400" />
        Notification emails
      </div>
      <div className="flex flex-wrap gap-1.5">
        {emails.map((e) => (
          <span
            key={e}
            className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-700 text-[11px] rounded-full"
          >
            {e}
          </span>
        ))}
      </div>
    </div>
  );
}

function EventTimeline({ events }) {
  if (!Array.isArray(events) || events.length === 0) return null;
  // Show most-recent first, capped at 10. The full history is in
  // port_request_events for support if needed.
  const recent = [...events].reverse().slice(0, 10);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-3 text-xs font-medium text-slate-700">
        <FileSignature className="w-3.5 h-3.5 text-slate-400" />
        Activity
      </div>
      <ul className="space-y-2">
        {recent.map((e) => (
          <li key={e.id} className="text-xs flex items-start gap-2">
            <span className="text-slate-400 font-mono whitespace-nowrap text-[11px] mt-0.5">
              {formatDateTimeShort(e.created_at)}
            </span>
            <span className="text-slate-700 flex-1 break-words">
              {labelizeEvent(e.event_type)}
              {e.to_status && e.to_status !== e.from_status && (
                <span className="text-slate-400">{` (${labelize(e.to_status)})`}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// Cancel-confirm modal
// ============================================================================

function CancelConfirmModal({ portRequest, reason, onReasonChange, onConfirm, onClose, canceling, error }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Cancel this port request?</h3>
            <p className="text-xs text-slate-500 mt-1">
              We will cancel the port request on Twilio and stop the port from completing. If a Letter of Authorization is still pending signature, Twilio will invalidate it automatically. Your numbers stay with their current carrier.
            </p>
          </div>
        </div>

        <label className="block">
          <span className="text-xs text-slate-600 mb-1 inline-block">Reason (optional, shown in the audit log)</span>
          <textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="e.g. Customer changed their mind, going with a different number."
            className="w-full px-3 py-2 text-xs text-slate-900 border border-slate-200 rounded-lg focus:outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100 resize-none"
          />
        </label>

        {error && (
          <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={canceling}
            className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50"
          >
            Keep the port
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={canceling}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg disabled:opacity-60"
          >
            {canceling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
            Cancel port
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function isCancelable(portRequest) {
  const CANCELABLE_STATUSES = new Set([
    'submitted',
    'in_carrier_review',
    'scheduled',
    'exception',
  ]);
  if (!CANCELABLE_STATUSES.has(portRequest.status)) return false;
  if (portRequest.status === 'scheduled' && portRequest.requested_foc_date) {
    const foc = new Date(`${portRequest.requested_foc_date}T00:00:00Z`);
    if (!Number.isNaN(foc.getTime()) && Date.now() >= foc.getTime()) return false;
  }
  return true;
}

function formatForDisplay(e164) {
  if (!e164 || e164.length < 12) return e164;
  const d = e164.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return e164;
}

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatDateTimeShort(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatDateOnly(d) {
  if (!d) return '';
  try {
    return new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return d;
  }
}

function labelize(s) {
  if (!s) return '';
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const EVENT_LABELS = {
  created: 'Draft created',
  draft_updated: 'Draft updated',
  bill_uploaded: 'Carrier bill uploaded',
  loa_envelope_created: 'LOA signing requested',
  loa_signed: 'LOA signed',
  loa_declined: 'LOA declined',
  submit_lock_acquired: 'Submit lock acquired',
  loa_decrypted_for_submit: 'Submit prepared',
  submitted_to_twilio: 'Submitted to Twilio',
  submit_rejected: 'Submit rejected',
  twilio_status_changed: 'Status updated by Twilio',
  twilio_status_unmapped: 'Unmapped Twilio status received',
  customer_action_required: 'Customer action required',
  canceled_by_user: 'Canceled by customer',
  completed_number_wired: 'Number wired up on SurFox',
};

function labelizeEvent(t) {
  return EVENT_LABELS[t] || labelize(t);
}
